import React, { Fragment, useEffect, useRef } from 'react';
import { Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { buildSelectOptionsFromArray } from '../../../../../../../../GlobalComponents/Helpers';

const OverviewSection = ({
    renderField,
    setValue,
    watch,
    sections,
    assessment,
    services,
    connections,
    isEditing,
    isAdmin,
}) => {
    const sourceSystem = watch('overview.source_system') || '';
    const destSystem = watch('overview.destination_system') || '';
    const prevSource = useRef(sourceSystem);
    const prevDest = useRef(destSystem);

    useEffect(() => {
        if (prevSource.current && prevSource.current !== sourceSystem) {
            setValue('overview.source_connection', '');
        }
        prevSource.current = sourceSystem;
    }, [sourceSystem]);

    useEffect(() => {
        if (prevDest.current && prevDest.current !== destSystem) {
            setValue('overview.destination_connection', '');
        }
        prevDest.current = destSystem;
    }, [destSystem]);

    const partnerId = assessment.partner_id;
    const sourceConnections = connections.filter(c => c.partner_id === partnerId && c.service === sourceSystem);
    const destConnections = connections.filter(c => c.partner_id === partnerId && c.service === destSystem);

    const serviceOptions = buildSelectOptionsFromArray(services, { label: 'name', value: 'name' });
    const jobTypeOptions = buildSelectOptionsFromArray([
        { id: 'migration', value: 'Data Migration' },
        { id: 'one-way-sync', value: 'One-Way Sync' },
        { id: 'two-way-sync', value: 'Two-Way Sync' },
    ], { label: 'value', value: 'id' });

    const sourceConnOptions = sourceConnections.length > 0
        ? buildSelectOptionsFromArray(sourceConnections, { label: 'name', value: 'connection_id' })
        : [{ label: sourceSystem ? 'No connections for ' + sourceSystem : 'Select a source system first', value: '', isDisabled: true }];

    const destConnOptions = destConnections.length > 0
        ? buildSelectOptionsFromArray(destConnections, { label: 'name', value: 'connection_id' })
        : [{ label: destSystem ? 'No connections for ' + destSystem : 'Select a destination system first', value: '', isDisabled: true }];

    const fieldSizes = { labelSize: 4, inputSize: 7 };
    const connFieldSizes = { labelSize: 4, inputSize: 7 };

    // Helper to render connection value as a link for admins in read-only mode
    const renderConnectionField = (sectionKey, fieldName, label, options) => {
        if (!isEditing && isAdmin) {
            const connId = watch(`${sectionKey}.${fieldName}`) || sections[sectionKey]?.[fieldName];
            const conn = connections.find(c => String(c.connection_id) === String(connId));
            const connName = conn?.name || connId || '—';
            const lblSize = options.labelSize || 4;
            const valSize = options.inputSize || 8;
            return (
                <div className='row mb-2'>
                    <div className={`col-sm-${lblSize}`}><strong>{label}</strong></div>
                    <div className={`col-sm-${valSize}`}>
                        {connId ? <Link className='txt-primary fw-bolder' to={`${process.env.PUBLIC_URL}/connections/${conn?.guid || connId}`} target='_blank' rel='noopener noreferrer'>{connName}</Link> : <span className='text-muted'>—</span>}
                    </div>
                </div>
            );
        }
        return renderField(sectionKey, fieldName, label, 'reactselect', options);
    };

    const sourceSystemLocked = (sections.phases?.rows || []).some(row => row.data_import);

    return (
        <Fragment>
            <Row>
                <Col md={{ size: 5, offset: 0 }}>
                    {renderField('overview', 'job_types', 'Job Type', 'reactselect', { ...fieldSizes, options: jobTypeOptions })}
                </Col>
            </Row>
            <Row>
                <Col md={{ size: 5, offset: 0 }}>
                    {sourceSystemLocked
                        ? renderField('overview', 'source_system', 'Source System', 'reactselect', { ...fieldSizes, options: serviceOptions, readOnly: true })
                        : renderField('overview', 'source_system', 'Source System', 'reactselect', { ...fieldSizes, options: serviceOptions })
                    }
                    {sourceSystemLocked && isEditing && <small className='txt-secondary' style={{ marginTop: -18, display: 'block', paddingLeft: '13.33%', marginBottom: 15 }}>System Locked — migration phases reference this system's resources</small>}
                </Col>
                {sourceSystem && (
                    <Col md={{ size: 5 }}>
                        {renderConnectionField('overview', 'source_connection', 'Source Connection', { ...connFieldSizes, options: sourceConnOptions })}
                    </Col>
                )}
            </Row>
            <Row>
                <Col md={5}>
                    {renderField('overview', 'destination_system', 'Destination System', 'reactselect', { ...fieldSizes, options: serviceOptions })}
                </Col>
                {destSystem && (
                    <Col md={5}>
                        {renderConnectionField('overview', 'destination_connection', 'Dest Connection', { ...connFieldSizes, options: destConnOptions })}
                    </Col>
                )}
            </Row>
            <Row>
                <Col md={10}>
                    {renderField('overview', 'scope_description', 'Scope', 'textarea', { labelSize: 2, inputSize: 9, offset: 0, viewLabelSize: 2, viewInputSize: 10, style: { height: 120 } })}
                </Col>
            </Row>
        </Fragment>
    );
};

export default OverviewSection;
